<script lang="ts">
  import { fade, fly } from 'svelte/transition'
  import { isCartDrawerOpen, isCartUpdating, isMenuDrawerOpen } from '../store/store'
  import { dispatchCartUpdated } from '../utils/cart-updated'
  import { formDataToCartAddPayload } from '../utils/cart-add'

  export let freeShippingThreshold: number = 7500
  export let cartUrl: string = '/cart'
  export let cart: { items: []; total_price: number; cart_level_discount_applications: [] } = {
    items: [],
    total_price: 0,
    cart_level_discount_applications: [],
  }

  import Money from './Money.svelte'
  import { clickOutside } from '../utils/click-outside'
  import CartItem from './CartItem.svelte'
  import CartComplementaryProducts from './CartComplementaryProducts.svelte'

  let signature = window.localStorage.getItem('signature') || 'Authority to leave their package'

  $: window.localStorage.setItem('signature', signature)

  let cartDrawerEl: HTMLDivElement

  function mapCartDiscounts(cartData: {
    cart_level_discount_applications?: Array<{ discount_application?: { title: string }; amount: number }>
  }) {
    return cartData.cart_level_discount_applications?.map((discount) => ({
      title: discount.discount_application?.title,
      value: discount.amount / 100,
    }))
  }

  function applyCartData(data: Record<string, unknown>) {
    cart = {
      ...cart,
      ...data,
      cart_level_discount_applications: mapCartDiscounts(
        data as Parameters<typeof mapCartDiscounts>[0],
      ),
    } as typeof cart
    dispatchCartUpdated(data)
  }

  function refreshCart() {
    $isCartUpdating = true
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        applyCartData(data)
        $isCartUpdating = false
        return data
      })
      .catch(() => {
        $isCartUpdating = false
      })
  }

  // Add classes to cart line items if cart is updating
  $: cartIsUpdatingClass = $isCartUpdating ? 'opacity-50 pointer-events-none' : ''

  // Add focus to cart drawer when it opens
  $: {
    if ($isCartDrawerOpen) {
      document.querySelector('body')?.classList.add('overflow-hidden')
      cartDrawerEl?.focus()
    }
  }

  function updateItem(item: { variant_id: number }, quantity: number) {
    $isCartUpdating = true
    const id = item.variant_id * 1

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [id]: quantity,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        applyCartData(result)
        $isCartUpdating = false
      })
      .catch(() => {
        $isCartUpdating = false
      })
  }

  function removeItem(item: { variant_id: number }) {
    return updateItem(item, 0)
  }

  function closeCartDrawer() {
    document.querySelector('body')?.classList.remove('overflow-hidden')
    isCartDrawerOpen.set(false)
  }

  function normalizeCartAddError(result: {
    message?: string
    description?: string | Record<string, string | string[]>
    errors?: Record<string, string | string[]>
  }) {
    const fieldErrors =
      result.errors ??
      (typeof result.description === 'object' && result.description !== null
        ? result.description
        : undefined)

    const message =
      typeof result.description === 'string'
        ? result.description
        : typeof result.message === 'string'
          ? result.message
          : 'Unable to add gift card to cart.'

    return { message, fieldErrors }
  }

  window.addToCartHandler = function (form: HTMLFormElement) {
    console.log(Array.from(new FormData(form).entries()))
    const payload = formDataToCartAddPayload(new FormData(form))

    $isCartUpdating = true

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) {
          const { message, fieldErrors } = normalizeCartAddError(result)

          document.dispatchEvent(
            new CustomEvent('cart:add:error', {
              detail: {
                variantId: payload.id,
                message,
                errors: fieldErrors,
              },
            }),
          )
          $isCartUpdating = false
          return
        }

        $isCartDrawerOpen = true
        return refreshCart()
      })
      .catch(() => {
        $isCartUpdating = false
      })
  }

  document.addEventListener('toggle-cart', () => {
    isMenuDrawerOpen.set(false)
    isCartDrawerOpen.set(true)
  })
</script>

{#if $isCartDrawerOpen}
  <div class="relative z-50" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
    <div
      in:fade={{ duration: 500 }}
      out:fade={{ duration: 500 }}
      class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
    />

    <div class="fixed inset-0 overflow-hidden">
      <div class="absolute inset-0 overflow-hidden">
        <div
          class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 focus:outline-none"
          tabindex="-1"
          use:clickOutside={() => closeCartDrawer()}
          bind:this={cartDrawerEl}
        >
          <div
            in:fly={{ duration: 500, x: 500, opacity: 100 }}
            out:fly={{ duration: 500, x: 500, opacity: 100 }}
            class="pointer-events-auto w-screen max-w-md"
          >
            <div
              class="flex h-full flex-col overflow-y-scroll dark:bg-neutral-900 bg-white shadow-xl"
            >
              <div class="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
                <div class="flex items-start justify-between">
                  <h2
                    class="text-2xl flex gap-4 items-center font-medium dark:text-gray-50 text-gray-900"
                    id="slide-over-title"
                  >
                    My Cart
                    {#if $isCartUpdating}
                      <svg
                        class="animate-spin -ml-1 mr-3 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    {/if}
                  </h2>
                  <div class="ml-3 flex h-7 items-center">
                    <button
                      on:click={() => closeCartDrawer()}
                      type="button"
                      class="-m-2 p-2 text-gray-400 dark:text-gray-200 hover:text-gray-500"
                    >
                      <span class="sr-only">Close</span>
                      <!-- Heroicon name: outline/x-mark -->
                      <svg
                        class="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {#if cart.total_price && freeShippingThreshold}
                  <div class="my-4">
                    {#if cart.total_price > freeShippingThreshold}
                      Congratulations! You have free shipping!
                    {:else}
                      You are <span class="text-primary"
                        ><Money
                          price={freeShippingThreshold - cart.total_price}
                          showCurrency={true}
                        /></span
                      > away from free shipping!
                    {/if}
                    <div class="rounded-md h-6 bg-gray-200 mt-2">
                      <div
                        class="rounded-md h-6 bg-primary transition-all relative"
                        style="width: {Math.min(cart.total_price / freeShippingThreshold, 1) * 100}%">
                          <div class="absolute -top-1 -right-1 rounded-full flex items-center justify-center h-8 w-8 bg-primary text-white">
                            
                            <svg class="size-4" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                              <path d="M0 96C0 60.7 28.7 32 64 32l288 0c35.3 0 64 28.7 64 64l0 32 50.7 0c17 0 33.3 6.7 45.3 18.7L557.3 192c12 12 18.7 28.3 18.7 45.3L576 384c0 35.3-28.7 64-64 64l-3.3 0c-10.4 36.9-44.4 64-84.7 64s-74.2-27.1-84.7-64l-102.6 0c-10.4 36.9-44.4 64-84.7 64s-74.2-27.1-84.7-64L64 448c-35.3 0-64-28.7-64-64L0 96zM512 288l0-50.7-45.3-45.3-50.7 0 0 96 96 0zM192 424a40 40 0 1 0 -80 0 40 40 0 1 0 80 0zm232 40a40 40 0 1 0 0-80 40 40 0 1 0 0 80z"/>
                            </svg>
                        </div>
                        </div>
                    </div>
                  </div>
                {/if}

                <div class="mt-8">
                  <div class="flow-root">
                    {#if cart?.items?.length > 0}
                      <!-- svelte-ignore a11y-no-redundant-roles -->
                      <ul role="list" class="space-y-6 {cartIsUpdatingClass}">
                        {#each cart.items as item, index (item.key)}
                          <CartItem
                            {removeItem}
                            {updateItem}
                            {item}
                            line={index + 1}
                            onConverted={refreshCart}
                          />
                        {/each}
                      </ul>

                      <CartComplementaryProducts cartItems={cart.items} onAdd={refreshCart} />
                    {:else if !$isCartUpdating}
                      <div class="text-center mt-12 md:mt-20">
                        <h2 class="text-2xl my-6 text-primary font-bold">Your cart is empty</h2>

                        <div class="space-y-4">
                          <a
                            class="block py-2 px-4 rounded-lg text-center border border-wagLight bg-wagLight/50"
                            href="/collections/all"
                          >
                            Shop all
                          </a>

                          <a
                            class="block py-2 px-4 rounded-lg text-center border border-wagLight bg-wagLight/50"
                            href="/collections/best-sellers"
                            >Best Sellers
                          </a>
                          <a
                            class="block py-2 px-4 rounded-lg text-center border border-wagLight bg-wagLight/50"
                            href="/collections/dog-treat-packs"
                            >Packs
                          </a>

                          <a
                            class="block py-2 px-4 rounded-lg text-center border border-wagLight bg-wagLight/50"
                            href="/collections/subscriptions"
                          >
                            Subscribe & Save
                          </a>
                        </div>
                      </div>
                    {/if}
                  </div>
                </div>
              </div>

              
              <div class="py-6 px-4 sm:px-6">
                {#if cart?.items.length > 0}
                  <form action={cartUrl} method="post">
                    {#if cart?.cart_level_discount_applications?.length > 0}
                      {#each cart.cart_level_discount_applications as discountAllocation}
                        <div
                          class="flex justify-between text-base font-medium dark:text-gray-50 text-gray-900"
                        >
                        
                          <p>Discount: {discountAllocation.title}</p>
                          <p>
                            -<Money price={discountAllocation.total_allocated_amount} showCurrency={true} />
                          </p>
                        </div>
                      {/each}
                    {/if}

                    <div
                      class="flex justify-between text-base font-medium dark:text-gray-50 text-gray-900"
                    >
                      <p>Your Cart ({cart.items.length}):</p>
                      <p>
                        <Money price={cart.total_price} showCurrency={true} />
                      </p>
                    </div>
                    <div
                      class="flex justify-between text-base my-4 font-medium dark:text-gray-50 text-gray-900"
                    >
                      <p>
                        <svg
                          class="w-4 h-4 inline-block mr-2"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.3337 12.3333V4.16667C13.3337 3.23325 13.3337 2.76654 13.152 2.41002C12.9922 2.09641 12.7372 1.84144 12.4236 1.68166C12.0671 1.5 11.6004 1.5 10.667 1.5H4.33366C3.40024 1.5 2.93353 1.5 2.57701 1.68166C2.2634 1.84144 2.00844 2.09641 1.84865 2.41002C1.66699 2.76654 1.66699 3.23325 1.66699 4.16667V9.66667C1.66699 10.6001 1.66699 11.0668 1.84865 11.4233C2.00844 11.7369 2.2634 11.9919 2.57701 12.1517C2.93353 12.3333 3.40024 12.3333 4.33366 12.3333H13.3337ZM13.3337 12.3333H17.0003C17.467 12.3333 17.7004 12.3333 17.8787 12.2425C18.0355 12.1626 18.1629 12.0351 18.2428 11.8783C18.3337 11.7001 18.3337 11.4667 18.3337 11V8.71895C18.3337 8.51513 18.3337 8.41321 18.3106 8.31731C18.2902 8.23228 18.2566 8.15099 18.2109 8.07643C18.1593 7.99233 18.0873 7.92027 17.9431 7.77614L16.2242 6.05719C16.0801 5.91306 16.008 5.841 15.9239 5.78947C15.8493 5.74378 15.768 5.71011 15.683 5.68969C15.5871 5.66667 15.4852 5.66667 15.2814 5.66667H13.3337M7.50033 14.4167C7.50033 15.5673 6.56759 16.5 5.41699 16.5C4.2664 16.5 3.33366 15.5673 3.33366 14.4167C3.33366 13.2661 4.2664 12.3333 5.41699 12.3333C6.56759 12.3333 7.50033 13.2661 7.50033 14.4167ZM16.667 14.4167C16.667 15.5673 15.7343 16.5 14.5837 16.5C13.4331 16.5 12.5003 15.5673 12.5003 14.4167C12.5003 13.2661 13.4331 12.3333 14.5837 12.3333C15.7343 12.3333 16.667 13.2661 16.667 14.4167Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>

                        Taxes and shipping calculated at checkout
                      </p>
                    </div>
                    <div class="my-4">
                      
                        <button
                          type="submit"
                          name="checkout"
                          class="bg-primary rounded-lg w-full text-white py-2 text-center"
                        >
                          <svg
                            class="w-4 h-4 mr-2 inline-block"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6.91802 18.0125C7.10251 18.1201 7.19475 18.1739 7.32494 18.2018C7.42597 18.2235 7.57338 18.2235 7.67441 18.2018C7.80459 18.1739 7.89684 18.1201 8.08133 18.0125C9.70471 17.0654 14.1663 14.0904 14.1663 10.0001V6.01472C14.1663 5.34846 14.1663 5.01533 14.0574 4.72898C13.9611 4.47601 13.8047 4.25029 13.6016 4.07133C13.3718 3.86875 13.0598 3.75178 12.436 3.51785L7.96784 1.84228C7.79459 1.77732 7.70797 1.74483 7.61885 1.73196C7.53981 1.72053 7.45954 1.72053 7.38049 1.73196C7.29138 1.74483 7.20476 1.77732 7.03151 1.84228L2.56334 3.51785C1.93951 3.75178 1.62759 3.86875 1.39772 4.07133C1.19466 4.25029 1.03824 4.47601 0.941975 4.72898C0.833008 5.01533 0.833008 5.34846 0.833008 6.01472V10.0001C0.833008 14.0904 5.29464 17.0654 6.91802 18.0125Z"
                              stroke="currentColor"
                              stroke-width="1.66667"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>

                          Secure checkout
                        </button>
                      
                    </div>
                  </form>
                {/if}
                
                <a
                  href="/"
                  class="border rounded-lg border-primary bg-primary text-white block py-2 text-center"
                >
                  <svg
                    class="w-4 h-4 mr-2 inline-block"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.1663 6H0.833008M0.833008 6L5.83301 11M0.833008 6L5.83301 1"
                      stroke="currentColor"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  Continue shopping
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}
